import { Easings } from "@scrolly/renderer";

// mostly adapted from https://github.com/nozzle/react-static/blob/57ecc92a63182aab91112be5c3e787c338308b09/packages/react-static/src/browser/utils/scrollTo.js

interface ScrollOptions {
  duration?: number
  offset?: number
  context?: Window | null | HTMLElement
  easing?: (t: number) => number
}

const defaultOptions: Required<ScrollOptions> = {
  duration: 800,
  offset: 0,
  context: typeof document !== 'undefined' ? window : null,
  easing: Easings.cubicInOut,
}

const getTop = (
  element: HTMLElement,
  offset: number,
  contextScrollHeight: number,
  contextVisibleHeight: number
) =>
  Math.min(
    element.getBoundingClientRect().top + window.pageYOffset + offset,
    contextScrollHeight - contextVisibleHeight
  )

const getPosition = (
  start: number,
  end: number,
  elapsed: number,
  duration: number,
  easeFn: (t: number) => number
) => {
  if (elapsed > duration) {
    return end
  }
  return start + (end - start) * easeFn(elapsed / duration)
}

export default function scrollTo(
  target: number | HTMLElement,
  options: ScrollOptions
) {
  const duration = options.duration ?? defaultOptions.duration
  const offset = options.offset ?? defaultOptions.offset
  const context = options.context ?? defaultOptions.context
  const easing = options.easing ?? defaultOptions.easing

  // when context is null, we don't scroll
  if (context === null) {
      return undefined;
  }
  let start = window.pageYOffset

  // context is an HTMLElement
  let innerHeight: TODOAny;
  let scrollHeight: TODOAny;
  if (context !== window) {
      start = (context as HTMLElement).scrollTop;
      innerHeight = (context as HTMLElement).offsetHeight;
      scrollHeight = (context as HTMLElement).scrollHeight;
  } else {
      // context is probably a window
      innerHeight = window.innerHeight;
      scrollHeight = document.body.scrollHeight;
  }
  const clock = Date.now() - 1
  let frameId: number;
  let isCancelled = false;

  return {
    promise: new Promise<void>((resolve) => {
      const step = () => {
          if (isCancelled) {
              resolve();
              return;
          }

          const elapsed = Date.now() - clock;
          const end =
              typeof target === "number" ? target : getTop(target as HTMLElement, offset, scrollHeight, innerHeight);
          if (context !== window) {
              (context as HTMLElement).scroll(0, getPosition(start, end, elapsed, duration, easing));
          } else {
              window.scroll(0, getPosition(start, end, elapsed, duration, easing));
          }

          if (typeof duration === "undefined" || elapsed > duration) {
              resolve();
              return;
          }

          // Sanity check to prevent taking over the scroll once we prematurely got to the element
          if (start === end) {
              resolve();
              return;
          }

          frameId = requestAnimationFrame(step);
      };
      frameId = requestAnimationFrame(step);
    }),
    cancel: () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      isCancelled = true;
    }
  };
}
